import React, { useEffect, ReactNode } from 'react';
import { FaCheckCircle, FaTimesCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

export interface ToastProps {
  message: ReactNode;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
  details?: string[];
}

const toastVariants = {
  success: {
    base: 'bg-success border-success text-on-semantic',
    text: 'text-on-semantic',
    Icon: FaCheckCircle,
  },
  error: {
    base: 'bg-error border-error text-on-semantic',
    text: 'text-on-semantic',
    Icon: FaTimesCircle,
  },
  info: {
    base: 'bg-primary border-primary text-on-primary',
    text: 'text-on-primary',
    Icon: FaInfoCircle,
  },
};

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'success',
  duration = 3000,
  onClose,
  details,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const variant = toastVariants[type];
  const { Icon } = variant;

  return (
    <div
      className={`flex items-center gap-3 px-5 py-3 rounded-full shadow-lg ${variant.base} min-w-[320px] max-w-lg animate-slide-down`}
    >
      <Icon className={variant.text} size={18} />
      <div className="flex-1">
        <span className={`text-sm font-medium ${variant.text} whitespace-pre-line`}>{message}</span>
        {details && details.length > 0 && (
          <div className="mt-2 p-2 bg-surface-primary rounded text-xs">
            {details.map((detail, index) => (
              <div key={index}>{detail}</div>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        className={`${variant.text} hover:opacity-70 transition-opacity ml-2`}
        data-testid="toast-close-button"
        aria-label="Dismiss"
      >
        <FaTimes size={14} />
      </button>
    </div>
  );
};

export default Toast;